import { Component, EventEmitter, OnInit, Output, ViewEncapsulation } from '@angular/core';
import { Router } from '@angular/router';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DEFAULT_EMAIL } from '../data/invitations.seed';
import { Invitation, InvitationDraft, Organizer, RosterRef, ScheduleStep } from '../models/invitation.model';
import { InvitationMockService } from '../services/invitation-mock.service';
import { wireScheduleRules } from '../schedule-validators';

@Component({
  selector: 'app-invitation-create',
  templateUrl: './invitation-create.component.html',
  styleUrls: ['./invitation-create.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class InvitationCreateComponent implements OnInit {
  currentStep = 1;
  readonly lastStep = 4;
  rosters: RosterRef[] = [];
  form!: FormGroup;

  @Output() saved = new EventEmitter<Invitation>();
  @Output() cancelled = new EventEmitter<void>();

  private datesFilled = false;

  constructor(private fb: FormBuilder, private svc: InvitationMockService, private router: Router) {}

  ngOnInit(): void {
    this.svc.getRosters().subscribe(r => this.rosters = r);
    this.form = this.fb.group({
      name: ['', Validators.required],
      rosterId: ['', Validators.required],
      language: ['English', Validators.required],
      estimatedParticipants: [10, [Validators.required, Validators.min(1)]],
      whoInvites: ['i_email', Validators.required],
      whoPays: ['i_pay', Validators.required],
      howResults: ['i_present', Validators.required],
      deadline: ['', Validators.required],
      presentation: ['', Validators.required],
      followupEmailsEnabled: [true],
      followupDate: [''],
      organizers: this.fb.array([]),
      emailSubject: [DEFAULT_EMAIL.subject, Validators.required],
      emailBody: [DEFAULT_EMAIL.body, Validators.required],
    });
    this.seedDefaultOrganizer();
    wireScheduleRules(this.form);
  }

  get organizers(): FormArray { return this.form.get('organizers') as FormArray; }

  /** Pre-seed the signed-in user as the first organizer (Invitation Admin) — prefilled but editable.
   * No notify: they're creating the invitation, so there's nothing to notify them about. */
  private seedDefaultOrganizer(): void {
    const me = this.svc.currentUser();
    this.organizers.push(this.fb.group({
      id: [''], name: [me.name], email: [me.email], role: ['owner'], notify: [false],
    }));
  }

  get selectedRoster(): RosterRef | undefined {
    return this.rosters.find(r => r.id === this.form.value.rosterId);
  }
  get keysShortfall(): number {
    const r = this.selectedRoster;
    return r ? Math.max(0, this.form.value.estimatedParticipants - r.availableKeys) : 0;
  }

  private static readonly DETAIL_FIELDS = ['name', 'rosterId', 'language', 'estimatedParticipants', 'whoPays', 'howResults'];
  private static readonly SCHEDULE_FIELDS = ['deadline', 'presentation', 'followupDate'];

  private detailsValid(): boolean {
    return InvitationCreateComponent.DETAIL_FIELDS.every(c => this.form.get(c)!.valid);
  }
  private scheduleValid(): boolean {
    return InvitationCreateComponent.SCHEDULE_FIELDS.every(c => this.form.get(c)!.valid);
  }
  private emailValid(): boolean {
    return this.form.get('emailSubject')!.valid && this.form.get('emailBody')!.valid;
  }

  next(): void { this.goToStep(this.currentStep + 1); }

  /** Jump to a step (header titles are clickable). Moving forward validates each step on the way. */
  goToStep(target: number): void {
    target = Math.max(1, Math.min(this.lastStep, target));
    if (target > this.currentStep) {
      for (let s = this.currentStep; s < target; s++) {
        if (s === 1 && !this.detailsValid()) { this.currentStep = 1; this.markFields(InvitationCreateComponent.DETAIL_FIELDS); return; }
        if (s === 2 && !this.scheduleValid()) { this.currentStep = 2; this.markFields(InvitationCreateComponent.SCHEDULE_FIELDS); return; }
      }
    }
    this.currentStep = target;
    if (this.currentStep === this.lastStep) { this.fillDateTokens(); }
  }

  /** One-time: replace {{deadline}}/{{presentation}} in the letter body with the step-2 dates. */
  private fillDateTokens(): void {
    if (this.datesFilled) { return; }
    const body = (this.form.value.emailBody as string) || '';
    const filled = body
      .replace(/\{\{deadline\}\}/g, this.fmtDate(this.form.value.deadline))
      .replace(/\{\{presentation\}\}/g, this.fmtDate(this.form.value.presentation));
    if (filled !== body) { this.form.get('emailBody')!.setValue(filled); }
    this.datesFilled = true;
  }

  private fmtDate(d: string): string {
    return d ? new Date(d).toLocaleDateString('en-US') : '';
  }
  back(): void { this.currentStep = Math.max(1, this.currentStep - 1); }
  cancel(): void { this.cancelled.emit(); }

  /** The link this invitation will get once created — shown resolved in the letter preview. */
  get assessmentLink(): string {
    return `${location.origin}/assessments/${this.svc.peekNextInvitationId()}`;
  }

  private markFields(names: string[]): void {
    names.forEach(c => this.form.get(c)!.markAsTouched());
  }

  /** bsDatepicker hands back Date objects; the model stores ISO strings. */
  private toIso(d: Date | string): string {
    return d ? new Date(d).toISOString() : '';
  }

  private buildDraft(): InvitationDraft {
    const v = this.form.value;
    const schedule: ScheduleStep[] = [
      { id: 's1', step: 1, name: 'deadline', eventName: 'Participant deadline', date: this.toIso(v.deadline) },
      { id: 's2', step: 2, name: 'presentation', eventName: 'Presentation', date: this.toIso(v.presentation) },
    ];
    if (v.followupEmailsEnabled && v.followupDate) {
      schedule.push({ id: 's3', step: 3, name: 'followup', eventName: 'First follow-up email', date: this.toIso(v.followupDate) });
    }
    return {
      name: v.name, rosterId: v.rosterId, language: v.language,
      estimatedParticipants: v.estimatedParticipants, whoInvites: v.whoInvites,
      whoPays: v.whoPays, howResults: v.howResults, schedule,
      organizers: v.organizers as Organizer[],
      email: { subject: v.emailSubject, body: v.emailBody },
      followupEmailsEnabled: v.followupEmailsEnabled,
    };
  }

  /** The wizard only ever creates an open invitation, so every step must validate first. */
  save(): void {
    if (!this.detailsValid()) {
      this.currentStep = 1; this.markFields(InvitationCreateComponent.DETAIL_FIELDS); return;
    }
    if (!this.scheduleValid()) {
      this.currentStep = 2; this.markFields(InvitationCreateComponent.SCHEDULE_FIELDS); return;
    }
    if (!this.emailValid()) {
      this.currentStep = this.lastStep;
      this.form.get('emailSubject')!.markAsTouched();
      this.form.get('emailBody')!.markAsTouched();
      return;
    }
    this.svc.create(this.buildDraft()).subscribe(inv => {
      this.saved.emit(inv); // let the host close the modal
      this.openInviteMail(inv);
      this.router.navigate(['/invitations', inv.id]);
    });
  }

  /** Open the mail client with the composed invite email (assessment link resolved to this invitation). */
  private openInviteMail(inv: Invitation): void {
    const link = `${location.origin}/assessments/${inv.id}`;
    const body = inv.email.body.replace(/\{\{assessment_link\}\}/g, link);
    window.open(`mailto:?subject=${encodeURIComponent(inv.email.subject)}&body=${encodeURIComponent(body)}`, '_blank');
  }
}
